import * as React from 'react';
import cx from 'classnames';
import useIsMounted from '@shopee/is-mounted-hook';

// Components
import {
  IconPhone24 as PhoneIcon,
  IconClose12 as CrossIcon,
  IconNavMeOutlined24 as UserIcon,
  IconPasswordLock24 as PasswordIcon,
  IconEyeClose24 as ShutEyeIcon,
  IconEyeOpen24 as OpenedEyeIcon,
  IconSearch16 as SearchIcon,
  IconCircleClose20 as CircleCrossIcon,
} from '@nebula/icon';
import { IconEmail } from './Icon/IconEmail';

// Utils
import {
  isWebBridgeSupported,
  getPlatformType,
  PlatformType,
} from '@shopee-user/platform';
import {
  resetNavbarPosition,
  scrollNavbarToTop,
} from '@tob/shared/components/GenericNavbar/utils/scroll-navbar';

// Styles
import styles from './style.scss';

type PureInputProps = {
  id?: string;
  className?: string;
  value?: string;
  type?: string;
  maxLength?: number;
  min?: string | number;
  max?: string | number;
  autoFocus?: boolean;
  autoComplete?: string;
  autoCorrect?: string;
  autoCapitalize?: string;
  placeholder?: string;
  disabled?: boolean;
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
  onChange: (event: React.SyntheticEvent<HTMLInputElement>) => void;
  onFocus: (event: React.SyntheticEvent<HTMLInputElement>) => void;
  onBlur: (event: React.SyntheticEvent<HTMLInputElement>) => void;
  clear?: () => void;
  reset?: () => void;
};

function PureInput({
  id,
  className,
  value,
  type = 'text',
  maxLength,
  max,
  min,
  autoFocus = false,
  autoComplete = 'on',
  autoCorrect = 'off',
  autoCapitalize = 'off',
  placeholder,
  disabled,
  inputRef,
  onChange,
  onFocus,
  onBlur,
}: PureInputProps) {
  const [focused, setFocused] = React.useState(false);
  const localInputRef = React.useRef<HTMLInputElement | null>(null);
  const isMounted = useIsMounted();

  // Preact autoFocus relies on autofocus from HTML5 and it doesn't always work
  // Need to imitate how React handles autoFocus
  // TODO: Enable auto focus on IOS if found any solutions
  React.useEffect(() => {
    if (autoFocus && localInputRef.current) {
      localInputRef.current.focus();
    }
  }, [autoFocus, localInputRef]);

  const onTouchMove = React.useCallback(() => {
    if (focused && localInputRef.current) {
      localInputRef.current.blur();
    }
  }, [localInputRef, focused]);

  const onInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    onFocus && onFocus(e);
    setFocused(true);
  };

  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlur && onBlur(e);
    isMounted.current && setFocused(false);
  };

  // Fix navigation bar scroll position issue when display keyboard on IOS webview
  React.useLayoutEffect(() => {
    const isIOSWebView =
      isWebBridgeSupported() && getPlatformType() === PlatformType.iOS;
    if (isIOSWebView) {
      if (focused) {
        window.addEventListener('touchmove', onTouchMove);

        scrollNavbarToTop();
      } else {
        window.removeEventListener('touchmove', onTouchMove);

        resetNavbarPosition();
      }
    }
  }, [focused, onTouchMove]);

  return (
    <input
      id={id}
      ref={el => {
        localInputRef.current = el;
        if (inputRef) {
          inputRef.current = el;
        }
      }}
      className={cx(className, styles.input, styles.inputDefault)}
      type={type}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={maxLength}
      max={max}
      min={min}
      autoFocus={autoFocus}
      autoComplete={autoComplete}
      autoCorrect={autoCorrect}
      autoCapitalize={autoCapitalize}
      onChange={onChange}
      onFocus={onInputFocus}
      onBlur={onInputBlur}
    />
  );
}

export type InputProps = PureInputProps & {
  wrapperClassName?: string;
  containerClassName?: string;
  inputClassName?: string;
  active?: boolean;
  hideClearButton?: boolean;
  leftContent?: JSX.Element;
  rightContent?: JSX.Element;
  clearIcon?: JSX.Element;
  showErrorOutline?: boolean;
  showError?: boolean;
  errors?: string[];
};

export function Input({
  wrapperClassName,
  containerClassName,
  inputClassName,
  leftContent,
  rightContent,
  clearIcon = <CrossIcon />,
  value,
  showErrorOutline = false,
  showError = false,
  errors = [],
  clear,
  active,
  hideClearButton = false,
  ...otherProps
}: InputProps) {
  // only show if there is value to be cleared and user's focus is in the input
  const showClearInputButton =
    value && value.length > 0 && active && !hideClearButton;
  return (
    <div className={wrapperClassName}>
      <div
        className={cx(
          containerClassName,
          styles.container,
          showErrorOutline && errors.length > 0 && styles.containerError
        )}
      >
        {leftContent}
        <PureInput {...otherProps} value={value} className={inputClassName} />
        {showClearInputButton && (
          <div
            className={styles.clickableIconContainer} // enlarge clickable area
            // use this instead of onclick because onclick doesn't work on this case.
            // onBlur event happens before onClick so the clear() will never fire bcs the element is already removed from the DOM
            // onMouseDown solves the issue. onMouseDown works the same way as onClick on most cases
            onMouseDown={clear}
          >
            {clearIcon}
          </div>
        )}
        {rightContent}
      </div>
      {!!showError && !!errors.length && (
        <div className={styles.errorContainer}>
          <div className={styles.error}>{errors[0]}</div>
        </div>
      )}
    </div>
  );
}

export function InputPhone(props: InputProps) {
  return (
    <Input
      type="tel"
      autoComplete="tel"
      className={styles.inputWithIcon}
      leftContent={
        <PhoneIcon
          className={cx(styles.leftIcon, styles.phoneIcon)}
          size={22}
        />
      }
      {...props}
    />
  );
}

export function InputEmail(props: InputProps) {
  return (
    <Input
      type="text"
      autoComplete="email"
      className={styles.inputWithIcon}
      leftContent={<IconEmail className={cx(styles.leftIcon)} />}
      {...props}
    />
  );
}

export function InputUsername(props: InputProps) {
  return (
    <Input
      type="text"
      autoComplete="username"
      className={styles.inputWithIcon}
      leftContent={
        <UserIcon className={cx(styles.leftIcon, styles.userIcon)} size={22} />
      }
      {...props}
    />
  );
}

export function InputSearch(props: InputProps) {
  return (
    <Input
      type="text"
      autoComplete="off"
      className={styles.inputSearch}
      containerClassName={styles.inputSearchContainer}
      leftContent={<SearchIcon className={cx(styles.searchIcon)} />}
      clearIcon={<CircleCrossIcon />}
      {...props}
    />
  );
}

export type InputPasswordProps = InputProps & {
  isHiddenInitial?: boolean;
  extraContent?: JSX.Element | null;
};
export function InputPassword({
  isHiddenInitial = true,
  extraContent,
  maxLength,
  className,
  ...otherProps
}: InputPasswordProps) {
  const [isHidden, setIsHidden] = React.useState<boolean>(isHiddenInitial);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const toggleHidden = React.useCallback(() => {
    setIsHidden(isHidden => !isHidden);

    // after is hidden is toggled, focus on the input again to prevent the keyboard from becoming hidden
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef]);

  const rightContent = (
    <>
      <div
        className={styles.clickableIconContainer} // enlarge clickable area
        onClick={toggleHidden}
      >
        {isHidden ? <ShutEyeIcon /> : <OpenedEyeIcon />}
      </div>
      {extraContent && (
        <>
          <div className={styles.separator} />
          {extraContent}
        </>
      )}
    </>
  );

  return (
    <Input
      type={isHidden ? 'password' : 'text'}
      autoComplete="current-password"
      leftContent={<PasswordIcon className={styles.leftIcon} />}
      rightContent={rightContent}
      maxLength={maxLength}
      inputRef={inputRef}
      className={cx(
        className,
        styles.inputWithIcon,
        isHidden && styles.inputPasswordHidden
      )}
      {...otherProps}
    />
  );
}
